/**
 * Client-side call suppression.
 *
 * WHY THIS EXISTS. The naive design calls an uncached REST endpoint on every page view. At
 * 500k+ visits a month that is 1.5M+ PHP-executing requests a month that the page cache
 * cannot absorb -- the one class of request this site's caching strategy has no answer
 * for. Suppression takes it from roughly one call per page view to roughly one per
 * session.
 *
 * THE RULE. Call the endpoint when ANY of these is true:
 *   1. there is no valid cc_vid cookie                       -- we have no identity yet;
 *   2. this page view carries campaign signal                -- there is something to record;
 *   3. there is no session marker in sessionStorage          -- first page view this session.
 * Otherwise skip the request entirely and populate forms from the cached snapshot.
 *
 * Every one of the three has to be checked. Dropping (1) loses visitors whose cookie was
 * cleared; dropping (2) loses a mid-session campaign click, which is a real touch;
 * dropping (3) means a returning visitor's session is never refreshed and their GA session
 * ID goes stale.
 *
 * sessionStorage is a browser write and is therefore gated on consent, like everything
 * else. Nothing in this module runs before the consent gate says granted.
 */

import { detectClickId, normaliseParams } from './classifier.js';

export const SESSION_KEY = 'cc_attr_session';
export const SNAPSHOT_KEY = 'cc_attr_snapshot';

const VISITOR_ID_PATTERN = /^[a-f0-9]{32}$/i;

/**
 * Whether a cookie string carries a well-formed cc_vid.
 *
 * A malformed value counts as absent: the server would reject it anyway, and treating it
 * as present would suppress the very call that would replace it.
 *
 * @param {string} cookieString Raw document.cookie value.
 * @param {string} [name]       Cookie name.
 * @return {boolean} True when a usable visitor ID is present.
 */
export function hasValidVisitorCookie( cookieString, name = 'cc_vid' ) {
	for ( const pair of String( cookieString || '' ).split( ';' ) ) {
		const index = pair.indexOf( '=' );

		if ( index !== -1 && pair.slice( 0, index ).trim() === name ) {
			return VISITOR_ID_PATTERN.test( decodeURIComponent( pair.slice( index + 1 ).trim() ) );
		}
	}

	return false;
}

/**
 * Whether this page view carries anything worth recording.
 *
 * Intentionally the same three-part test the classifier's rule-1 base case uses: any
 * campaign parameter, any click ID, or any external referrer.
 *
 * @param {object} signals Page view signal from signalsFromUrl().
 * @param {object} rules   Rule accessor.
 * @return {boolean} True when the page view carries campaign signal.
 */
export function hasCampaignSignal( signals, rules ) {
	const params = normaliseParams( signals.params );

	for ( const field of [ 'source', 'medium', 'campaign', 'content', 'term' ] ) {
		for ( const name of rules.campaignParams( field ) ) {
			if ( params[ String( name ).toLowerCase() ] ) {
				return true;
			}
		}
	}

	if ( detectClickId( params, rules ) !== null ) {
		return true;
	}

	let host = '';

	try {
		host = signals.referrer ? new URL( signals.referrer ).hostname.toLowerCase().replace( /\.+$/, '' ) : '';
	} catch {
		host = '';
	}

	// A return from a sign-in or payment provider is not campaign signal either.
	return host !== '' && ! rules.isInternalHost( host ) && ! rules.isExcludedReferrer( host );
}

/**
 * THE DECISION. Pure, so every branch of it is testable.
 *
 * @param {object}  state              State.
 * @param {boolean} state.hasCookie    A valid cc_vid cookie is present.
 * @param {boolean} state.hasSignal    This page view carries campaign signal.
 * @param {boolean} state.hasSession   A session marker exists in sessionStorage.
 * @return {boolean} True when the REST endpoint should be called.
 */
export function shouldCallEndpoint( { hasCookie, hasSignal, hasSession } ) {
	return ! hasCookie || Boolean( hasSignal ) || ! hasSession;
}

/**
 * Explains the decision, for the debug flag and for tests.
 *
 * @param {object} state Same shape as shouldCallEndpoint().
 * @return {string} A reason code.
 */
export function callReason( { hasCookie, hasSignal, hasSession } ) {
	if ( ! hasCookie ) {
		return 'no_cookie';
	}

	if ( hasSignal ) {
		return 'campaign_signal';
	}

	if ( ! hasSession ) {
		return 'new_session';
	}

	return 'suppressed';
}

/**
 * A session-scoped store that never throws.
 *
 * sessionStorage throws on access in Safari private mode and when a browser is configured
 * to block storage. An exception here would abort the whole capture script, so every
 * access degrades to "no cached state", which simply means the endpoint gets called.
 *
 * @param {object} [storage] sessionStorage-like object.
 * @return {object} Store.
 */
export function createSessionStore( storage ) {
	function read( key ) {
		try {
			return storage ? storage.getItem( key ) : null;
		} catch {
			return null;
		}
	}

	function write( key, value ) {
		try {
			if ( storage ) {
				storage.setItem( key, value );
			}
			return true;
		} catch {
			return false;
		}
	}

	return {
		hasSession() {
			return read( SESSION_KEY ) !== null;
		},

		markSession() {
			write( SESSION_KEY, '1' );
		},

		readSnapshot() {
			const raw = read( SNAPSHOT_KEY );

			if ( ! raw ) {
				return null;
			}

			try {
				const parsed = JSON.parse( raw );
				return parsed && typeof parsed === 'object' ? parsed : null;
			} catch {
				return null;
			}
		},

		writeSnapshot( snapshot ) {
			try {
				write( SNAPSHOT_KEY, JSON.stringify( snapshot ) );
			} catch {
				// A snapshot that cannot be serialised is not worth failing the page for.
			}
		},
	};
}

/**
 * Resolves the full decision for a page view.
 *
 * @param {object} options          Options.
 * @param {object} options.signals  Page view signal.
 * @param {object} options.rules    Rule accessor.
 * @param {string} options.cookies  Raw document.cookie value.
 * @param {object} options.store    Session store from createSessionStore().
 * @return {{call: boolean, reason: string, hasCookie: boolean, hasSignal: boolean, hasSession: boolean}} Decision.
 */
export function decide( { signals, rules, cookies, store } ) {
	const state = {
		hasCookie: hasValidVisitorCookie( cookies ),
		hasSignal: hasCampaignSignal( signals, rules ),
		hasSession: store.hasSession(),
	};

	return {
		...state,
		call: shouldCallEndpoint( state ),
		reason: callReason( state ),
	};
}
